import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FaBolt, FaBriefcase, FaBuilding, FaCalendarCheck, FaMapMarkerAlt, FaUsers } from 'react-icons/fa';
import SEOHead from '../components/SEOHead';
import { API_URL } from '../services/api';
import api from '../services/api';
import { getJobLocationLabel } from '../utils/jobLocationPresentation';
import {
  FEATURED_MARKET_SLUGS,
  HIRE_ROLES,
  MARKET_DATA,
  VENDOR_SPECIALTIES,
  getHireRole,
  getJobQuery,
  getMarket,
  getVendorQuery,
} from '../data/seoLandingData';

interface Vendor {
  id: number;
  slug: string | null;
  name: string;
  city: string;
  state: string;
  specialty: string;
  is_claimed: boolean;
}

interface Job {
  id: number;
  title: string;
  company: string;
  location: string | null;
  source: string;
}

const vendorSpecialtyLabel = Object.values(VENDOR_SPECIALTIES).reduce((acc, specialty) => {
  acc[specialty.apiValue] = specialty.label;
  return acc;
}, {} as Record<string, string>);

const HireRoleCityPage: React.FC = () => {
  const { role: roleSlug, city } = useParams<{ role: string; city?: string }>();
  const role = getHireRole(roleSlug);
  const market = city ? getMarket(city) : undefined;
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);

  useEffect(() => {
    if (!role || !market) return;

    fetch(`${API_URL}/api/vendors?${getVendorQuery(market, undefined, 4)}`)
      .then(r => r.json())
      .then(data => setVendors(data.vendors || []))
      .catch(() => setVendors([]));

    api.get(`/scraped-jobs?${getJobQuery(market, role, 4)}`)
      .then((res: any) => {
        const data = res.data?.jobs ?? res.data ?? [];
        setJobs(Array.isArray(data) ? data : []);
      })
      .catch(() => setJobs([]));
  }, [role, market]);

  if (!role || (city && !market)) {
    return <Navigate to="/for-employers" replace />;
  }

  const pagePath = market ? `/hire/${role.slug}/${market.slug}` : `/hire/${role.slug}`;
  const pageUrl = `https://stagecrew.app${pagePath}`;
  const title = market
    ? `Hire ${role.pluralLabel} in ${market.name}, ${market.stateAbbr} | StageCrew`
    : `Hire ${role.pluralLabel} for Live Events | StageCrew`;
  const description = market
    ? `Hire ${role.pluralLabel} in ${market.name}, ${market.stateAbbr}. ${role.description} Post a gig and reach local event crew.`
    : `Hire ${role.pluralLabel} for concerts, corporate events, conventions, festivals, and live productions. ${role.description}`;

  const marketList = market
    ? [market.slug, ...market.nearbyMarkets].filter((slug, index, arr) => arr.indexOf(slug) === index)
    : FEATURED_MARKET_SLUGS;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title={title}
        description={description}
        url={pageUrl}
        keywords={market ? `hire ${role.pluralLabel} ${market.name}, ${market.name} ${role.label}, event crew ${market.name}` : `hire ${role.pluralLabel}, event crew hiring, AV staffing, stagehand staffing`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: market ? `Hire ${role.pluralLabel} in ${market.name}` : `Hire ${role.pluralLabel}`,
          description,
          serviceType: 'Live event staffing',
          areaServed: market ? { '@type': 'City', name: market.name, addressRegion: market.stateAbbr } : 'United States',
          provider: { '@type': 'Organization', name: 'StageCrew', url: 'https://stagecrew.app' },
        }}
      />

      <section className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-4">
            <Link to="/for-employers" className="hover:text-blue-700">For Employers</Link>
            <span>/</span>
            <span>Hire {role.pluralLabel}</span>
            {market && (
              <>
                <span>/</span>
                <span>{market.name}</span>
              </>
            )}
          </div>
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700 mb-4">
              <FaUsers /> Live event staffing
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-950 tracking-normal mb-5">
              {market ? `Hire ${role.pluralLabel} in ${market.name}` : `Hire ${role.pluralLabel} for live events`}
            </h1>
            <p className="text-lg text-slate-600 max-w-3xl">
              {market ? `${market.buyerSummary} ` : ''}
              StageCrew helps employers reach {role.pluralLabel} for {role.buyerNeed}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/post-gig" className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-5 py-3 font-semibold text-white hover:bg-blue-800">
                <FaBolt /> Post a gig
              </Link>
              <Link to={market ? `/vendors/city/${market.slug}` : '/vendors'} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 hover:border-blue-400 hover:text-blue-700">
                <FaBuilding /> Browse vendors
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-10 grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <FaBriefcase className="text-blue-700 mb-3" />
          <h2 className="text-lg font-bold text-slate-950">Rate guidance</h2>
          <p className="text-sm text-slate-600 mt-2">{role.dayRate}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <FaCalendarCheck className="text-emerald-700 mb-3" />
          <h2 className="text-lg font-bold text-slate-950">Role scope</h2>
          <p className="text-sm text-slate-600 mt-2">{role.description}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <FaMapMarkerAlt className="text-amber-600 mb-3" />
          <h2 className="text-lg font-bold text-slate-950">{market ? market.name : 'Market guides'}</h2>
          <p className="text-sm text-slate-600 mt-2">{market ? market.localInsight : 'Review current local job and vendor inventory; city links are not a market ranking.'}</p>
        </div>
      </section>

      {market && (
        <section className="bg-white border-y border-slate-200">
          <div className="max-w-6xl mx-auto px-4 py-10 grid gap-8 lg:grid-cols-2">
            <div>
              <div className="flex items-center justify-between gap-4 mb-4">
                <h2 className="text-2xl font-bold text-slate-950">Local vendors</h2>
                <Link to={`/vendors/city/${market.slug}`} className="text-sm font-semibold text-blue-700 hover:underline">View directory</Link>
              </div>
              <div className="space-y-3">
                {vendors.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-300 p-6 text-sm text-slate-500">Vendor listings are still being expanded for {market.name}.</div>
                ) : vendors.map(vendor => (
                  <Link key={vendor.id} to={`/vendors/${vendor.slug || vendor.id}`} className="block rounded-lg border border-slate-200 p-4 hover:border-blue-400 transition">
                    <div className="font-semibold text-slate-950">{vendor.name}</div>
                    <div className="text-sm text-slate-500">{vendor.city}, {vendor.state} - {vendorSpecialtyLabel[vendor.specialty] || 'Vendor'}</div>
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between gap-4 mb-4">
                <h2 className="text-2xl font-bold text-slate-950">Recent matching gigs</h2>
                <Link to={`/gigs/${market.slug}/${role.gigSlug}`} className="text-sm font-semibold text-blue-700 hover:underline">View gigs</Link>
              </div>
              <div className="space-y-3">
                {jobs.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-300 p-6 text-sm text-slate-500">Live {role.label.toLowerCase()} gigs update as jobs are scraped.</div>
                ) : jobs.map(job => (
                  <Link key={job.id} to={`/jobs/${job.id}`} className="block rounded-lg border border-slate-200 p-4 hover:border-blue-400 transition">
                    <div className="font-semibold text-slate-950">{job.title}</div>
                    <div className="text-sm text-slate-500">
                      {job.company} - {getJobLocationLabel(job)}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-4 py-10 grid gap-8 lg:grid-cols-2">
        <div>
          <h2 className="text-2xl font-bold text-slate-950 mb-4">
            {market ? `Other roles in ${market.name}` : 'Hire by role'}
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {Object.values(HIRE_ROLES).filter(other => other.slug !== role.slug).slice(0, 8).map(other => (
              <Link key={other.slug} to={market ? `/hire/${other.slug}/${market.slug}` : `/hire/${other.slug}`} className="rounded-lg border border-slate-200 bg-white p-4 hover:border-blue-400 hover:bg-blue-50 transition">
                <div className="font-semibold text-slate-950">Hire {other.pluralLabel}</div>
                <div className="text-sm text-slate-500 mt-1">{other.dayRate}</div>
              </Link>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-2xl font-bold text-slate-950 mb-4">
            {market ? 'Nearby markets' : 'Hiring market guides'}
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {marketList.slice(0, 8).map(slug => {
              const nextMarket = MARKET_DATA[slug];
              if (!nextMarket) return null;
              return (
                <Link key={slug} to={`/hire/${role.slug}/${nextMarket.slug}`} className="rounded-lg border border-slate-200 bg-white p-4 hover:border-blue-400 hover:bg-blue-50 transition">
                  <div className="font-semibold text-slate-950">{nextMarket.name}</div>
                  <div className="text-sm text-slate-500 mt-1">{nextMarket.stateAbbr}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HireRoleCityPage;
